import type {
  CaAssignBody,
  CaAssignResult,
  CaSuggestionWire,
} from '../ca-lead/caSuggestion';
import { parseComplexitySource } from '../ca-lead/caSuggestion';
import {
  activityQuery,
  toActivityItem,
  toActivityPage,
  toAgentsPageAccess,
} from './activityWire';
import {
  toAgentsCatalog,
  toMemoryList,
  toSkillDoc,
  toSkillDraft,
  toSkillHistory,
  toSkillRefusal,
  toSkillVersion,
} from './agentsWire';
import {
  toMimirChannels,
  toMimirPreview,
  toMimirRefusal,
  toMimirSendOutcome,
} from './mimirWire';
import { toCardActionBody, toCardActionResult, toErrorCode } from './crewWire';
import { createSseParser } from './parseSse';
import type {
  ActivityFilters,
  ActivityItem,
  ActivityPage,
  AgentEvent,
  AgentsCatalog,
  AgentsPageAccess,
  CardAction,
  CardActionResult,
  ChatAccepted,
  ChatRequest,
  MemoryList,
  MimirChannels,
  MimirOutcome,
  MimirPreview,
  MimirSendInput,
  PricingUpload,
  Rating,
  SkillDoc,
  SkillDraft,
  SkillHistory,
  SkillRefusal,
  SkillVersion,
} from './types';
import {
  GENERIC_FAILURE,
  toAgentEvent,
  toChatAccepted,
  toChatBody,
  toFeedbackBody,
} from './wire';

export type AgentErrorKind =
  | 'access'
  | 'run_gone'
  | 'lost'
  | 'http'
  | 'card_closed';

/**
 * A failure whose message is safe to show. Messages are written here: never
 * copied from a response body, never containing the token.
 */
export class AgentApiError extends Error {
  readonly kind: AgentErrorKind;
  /** The service marked the card used before refusing, so it can't be pressed again. */
  readonly closesCard: boolean;
  /** Set when a skill edit was refused with a coded body (the UI reads `found` / `live`). */
  readonly refusal?: SkillRefusal;

  constructor(
    kind: AgentErrorKind,
    message: string,
    closesCard = false,
    refusal?: SkillRefusal,
  ) {
    super(message);
    this.refusal = refusal;
    this.name = 'AgentApiError';
    this.kind = kind;
    this.closesCard = closesCard;
  }
}

const MESSAGES = {
  access:
    "You don't have access, or your session has expired. Sign in again and retry.",
  // A 404 means a restart or someone else's run; the server doesn't say which.
  runGone: 'This run is no longer available. Ask again.',
  lost: 'Lost the connection to the agent service. Please start again.',
  busy: 'The previous answer is still running. Wait for it to finish, then ask again.',
  authUnavailable:
    "The agent service can't check your sign-in right now. Try again shortly.",
  cardClosed: 'This card has already been used.',
  notAllowed: 'Only mortgage staff can approve or reject a pack.',
  reasonRequired: 'Add a reason to reject the pack.',
  cardInvalid:
    "That action isn't available on this card. Start a new chat and try again.",
  agentGone: "That agent or skill isn't available.",
  memoryGone: 'That memory item is already gone.',
  notPublisher: "Only this skill's publisher can publish it or roll it back.",
  noDraft:
    "You have no draft of this skill. Save a draft first; to publish someone else's, copy it into your draft.",
  draftNotFound: 'That draft no longer exists. Refresh the list.',
  versionNotFound: "That version isn't a published version of this skill.",
  liveMoved:
    'The live version changed since you started — review the difference and save again.',
  alreadyLive: 'That version is already live.',
  clientData:
    'This text looks like it holds client details. Skills hold method, never client data. Nothing was saved.',
  tooLong:
    'This skill is longer than the 32,000-character limit. Nothing was saved.',
  badFrontmatter:
    "The text must start with the name and description between '---' lines. Nothing was saved.",
  nameMismatch:
    "The name at the top must stay the skill's own name. Nothing was saved.",
  empty: 'The skill needs a description and a body. Nothing was saved.',
  skillsUnavailable: "Skills can't be reached right now. Try again shortly.",
  memoryDown: "Memory can't be reached right now. Try again shortly.",
  fileTooLarge: 'This file is over 15 MB.',
  fileUnsupported: 'Drop a PDF, PNG or JPG.',
  importDown: "Rate sheet import isn't available right now.",
  importNotAllowed: "Your role can't import pricing.",
  batchExpired: 'This import has expired; drop the sheet again.',
  batchDiscarded: 'This import was already discarded. Drop the sheet again.',
  batchApplied: 'This import was already applied.',
  notProposer: 'Only the person who dropped the sheet can apply or discard it.',
  batchNotFound: "BrokerCopilot can't find this import. Drop the sheet again.",
  invalidSelection: 'Tick at least one change that can be applied.',
  productNotOnLender:
    "A row isn't one of this lender's products. Drop the sheet again.",
  batchStale:
    'Newer pricing was applied to this lender after this import. Drop the sheet again.',
  partialReplace:
    'Rows that replace the same current row must be applied together.',
  toolsUnavailable: "BrokerCopilot didn't answer. Try again.",
  agentsPageNotAllowed:
    'Only the superadmin and the skill publishers can open the Agents page.',
  mimirNotApprover: "Only Mimir's approvers can send from here.",
  mimirDown: "Mimir can't be reached right now. Try again shortly.",
  historyDown: "Can't load history right now. Try again shortly.",
  activityGone: "That run isn't in the history.",
  caNotAllowed: 'Only the CA lead and admins can use Suggest CA.',
  caUnknownRun: 'This suggestion has expired. Press Suggest again.',
  badReply: 'The agent service sent an unexpected reply. Try again.',
  unreachable:
    "Couldn't reach the agent service. Check your connection and try again.",
};

/** The live_moved copy, also shown as the editor's notice for a draft behind live. */
export const LIVE_MOVED_TEXT = MESSAGES.liveMoved;
/** The too_long copy, also used by the editor's own length check before a save. */
export const TOO_LONG_TEXT = MESSAGES.tooLong;

const httpError = (status: number) =>
  new AgentApiError(
    'http',
    `The agent service couldn't take the request (HTTP ${status}). Try again.`,
  );

// The service marks the card used before it calls the backend, so a coded
// refusal closes it (closesCard). Its own pre-checks run while the card is
// still open (invalid_selection, partial_replace), and 503 tools_unavailable
// re-opens it for a retry.
const PRICING_CARD_ERRORS: Record<
  string,
  { text: string; closesCard: boolean }
> = {
  '409 batch_expired': { text: MESSAGES.batchExpired, closesCard: true },
  '409 batch_discarded': { text: MESSAGES.batchDiscarded, closesCard: true },
  '409 batch_applied': { text: MESSAGES.batchApplied, closesCard: true },
  '409 batch_stale': { text: MESSAGES.batchStale, closesCard: true },
  '403 not_proposer': { text: MESSAGES.notProposer, closesCard: true },
  '404 batch_not_found': { text: MESSAGES.batchNotFound, closesCard: true },
  '422 invalid_selection': {
    text: MESSAGES.invalidSelection,
    closesCard: false,
  },
  '422 partial_replace': { text: MESSAGES.partialReplace, closesCard: false },
  '422 product_not_on_lender': {
    text: MESSAGES.productNotOnLender,
    closesCard: true,
  },
  // Safe to press Confirm again: the backend returns the stored result for an applied batch.
  '503 tools_unavailable': {
    text: MESSAGES.toolsUnavailable,
    closesCard: false,
  },
};

/** Card-action failures, told apart by the reply's `detail` code (Plan 4a's table). */
function cardError(status: number, code: string): AgentApiError {
  if (status === 403 && code === 'not_allowed') {
    return new AgentApiError('http', MESSAGES.notAllowed);
  }
  // Pricing import (the backend's codes, passed through by the agent service).
  // Fixed text only: never the backend's own wording.
  const key = `${status} ${code}`;
  if (Object.prototype.hasOwnProperty.call(PRICING_CARD_ERRORS, key)) {
    const { text, closesCard } = PRICING_CARD_ERRORS[key];
    return new AgentApiError('http', text, closesCard);
  }
  if (status === 401 || status === 403)
    return new AgentApiError('access', MESSAGES.access);
  if (status === 404) {
    return code === 'run_not_found'
      ? new AgentApiError('run_gone', MESSAGES.runGone)
      : new AgentApiError('http', MESSAGES.cardInvalid);
  }
  if (status === 409) {
    return code === 'card_closed'
      ? new AgentApiError('card_closed', MESSAGES.cardClosed)
      : new AgentApiError('http', MESSAGES.busy);
  }
  if (status === 422) {
    return code === 'reason_required'
      ? new AgentApiError('http', MESSAGES.reasonRequired)
      : new AgentApiError('http', MESSAGES.cardInvalid);
  }
  if (status === 503)
    return new AgentApiError('http', MESSAGES.authUnavailable);
  return httpError(status);
}

/** Rate-sheet upload failures, told apart by status and the reply's `detail` code. */
function uploadError(status: number, code: string): AgentApiError {
  if (status === 403 && code === 'not_allowed') {
    return new AgentApiError('http', MESSAGES.importNotAllowed);
  }
  if (status === 401 || status === 403)
    return new AgentApiError('access', MESSAGES.access);
  if (status === 409) return new AgentApiError('http', MESSAGES.busy);
  if (status === 413) return new AgentApiError('http', MESSAGES.fileTooLarge);
  if (status === 415)
    return new AgentApiError('http', MESSAGES.fileUnsupported);
  if (status === 503) return new AgentApiError('http', MESSAGES.importDown);
  return httpError(status);
}

/** Agents-view failures (Plan 5), told apart by the reply's `detail` code. */
function agentsError(status: number, code: string): AgentApiError {
  if (status === 401 || status === 403)
    return new AgentApiError('access', MESSAGES.access);
  if (status === 404) {
    return new AgentApiError(
      'http',
      code === 'memory_not_found' ? MESSAGES.memoryGone : MESSAGES.agentGone,
    );
  }
  if (status === 503) {
    return new AgentApiError(
      'http',
      code === 'memory_unavailable'
        ? MESSAGES.memoryDown
        : MESSAGES.authUnavailable,
    );
  }
  return httpError(status);
}

const SKILL_REFUSALS: Record<string, string> = {
  not_publisher: MESSAGES.notPublisher,
  no_draft: MESSAGES.noDraft,
  draft_not_found: MESSAGES.draftNotFound,
  version_not_found: MESSAGES.versionNotFound,
  live_moved: MESSAGES.liveMoved,
  already_live: MESSAGES.alreadyLive,
  client_data: MESSAGES.clientData,
  too_long: MESSAGES.tooLong,
  bad_frontmatter: MESSAGES.badFrontmatter,
  name_mismatch: MESSAGES.nameMismatch,
  empty: MESSAGES.empty,
  skills_unavailable: MESSAGES.skillsUnavailable,
};

/** Skill-edit failures: a coded refusal gets its fixed text and `refusal`; the rest map by status. */
function skillError(status: number, body: unknown): AgentApiError {
  const refusal = toSkillRefusal(body);
  if (
    refusal &&
    Object.prototype.hasOwnProperty.call(SKILL_REFUSALS, refusal.code)
  ) {
    return new AgentApiError(
      'http',
      SKILL_REFUSALS[refusal.code],
      false,
      refusal,
    );
  }
  const base = agentsError(status, toErrorCode(body));
  return refusal
    ? new AgentApiError(base.kind, base.message, false, refusal)
    : base;
}

/** Agents-page failures: 403 and 503 and 404 get their own copy, the rest as agentsError. */
function activityError(status: number, body: unknown): AgentApiError {
  const code = toErrorCode(body);
  if (status === 403 && code === 'not_allowed')
    return new AgentApiError('http', MESSAGES.agentsPageNotAllowed);
  if (status === 503 && code === 'history_unavailable')
    return new AgentApiError('http', MESSAGES.historyDown);
  if (status === 404 && code === 'activity_not_found')
    return new AgentApiError('http', MESSAGES.activityGone);
  return agentsError(status, code);
}

/** Mimir channel-list failures: its two coded refusals get their own copy, the rest as agentsError. */
function mimirError(status: number, body: unknown): AgentApiError {
  const code = toErrorCode(body);
  if (status === 403 && code === 'not_approver')
    return new AgentApiError('http', MESSAGES.mimirNotApprover);
  if (status === 503 && code === 'mimir_unavailable')
    return new AgentApiError('http', MESSAGES.mimirDown);
  return agentsError(status, code);
}

/** True for a 404 memory_not_found: the item is already gone, so a delete has nothing left to do. */
export const isMemoryGone = (error: unknown): boolean =>
  error instanceof AgentApiError && error.message === MESSAGES.memoryGone;

/** What the panel shows for any failure. */
export const toSafeMessage = (error: unknown): string =>
  error instanceof AgentApiError ? error.message : MESSAGES.unreachable;

export type AgentClientOptions = {
  baseUrl: string;
  getToken: () => string | null;
  fetchImpl?: typeof fetch;
  /** Consecutive reconnects that bring no new event before giving up. */
  maxReconnects?: number;
  retryDelayMs?: number;
};

export type StreamHandlers = {
  onEvent: (event: AgentEvent) => void;
  onReconnect?: () => void;
};

export type AgentClient = {
  /** POST /chat (202). Resolves with the run id and the server's session id. */
  startChat(request: ChatRequest, signal?: AbortSignal): Promise<ChatAccepted>;
  /**
   * POST /pricing-imports (202): a lender's rate sheet as multipart form data.
   * Resolves like startChat; the import's steps stream on the run's events.
   */
  uploadPricingSheet(
    upload: PricingUpload,
    signal?: AbortSignal,
  ): Promise<ChatAccepted>;
  /** GET /runs/{runId}/events until done (or error), resuming with Last-Event-ID. */
  streamEvents(
    runId: string,
    handlers: StreamHandlers,
    signal: AbortSignal,
  ): Promise<void>;
  /** POST /runs/{runId}/feedback (204). Never throws; false if it didn't land. */
  sendFeedback(runId: string, rating: Rating): Promise<boolean>;
  /**
   * POST /runs/{runId}/cards/{cardId} (Plan 4a). `runId` is the run whose stream
   * carried the card. start -> the new crew run; approve/reject -> the record.
   */
  cardAction(
    runId: string,
    cardId: string,
    action: CardAction,
    reason?: string,
    signal?: AbortSignal,
    /** confirm on a pricing card: the ticked row ids. */
    selected?: string[],
  ): Promise<CardActionResult>;
  /** POST /ca-lead/suggest: the Suggest CA card for an assessment. */
  suggestCa(assessmentId: string): Promise<CaSuggestionWire>;
  /**
   * POST /ca-lead/assign. Resolves whenever the body carries an `outcome` (200, 409, 422, 503);
   * throws only for a reply without one (403, 404, network).
   */
  assignCa(body: CaAssignBody): Promise<CaAssignResult>;
  /** GET /agents (Plan 5): the five agents, in crew order. */
  listAgents(signal?: AbortSignal): Promise<AgentsCatalog>;
  /** GET /agents/{agent}/skills/{skill}: one skill's markdown. */
  getSkill(
    agent: string,
    skill: string,
    signal?: AbortSignal,
  ): Promise<SkillDoc>;
  /** GET /agents/{agent}/skills/{skill}/versions: published versions and drafts. */
  getSkillHistory(
    agent: string,
    skill: string,
    signal?: AbortSignal,
  ): Promise<SkillHistory>;
  /** PUT …/draft: save the caller's own draft; replies with it. `copiedFrom` names a draft this one copies. */
  saveSkillDraft(
    agent: string,
    skill: string,
    text: string,
    copiedFrom?: string,
  ): Promise<SkillDraft>;
  /** DELETE …/draft (204). */
  discardSkillDraft(agent: string, skill: string): Promise<void>;
  /** POST …/publish: the caller's draft becomes the live version; replies with it. */
  publishSkill(
    agent: string,
    skill: string,
    reason: string,
  ): Promise<SkillVersion>;
  /** POST …/rollback: an earlier published version becomes live again, as a new version. */
  rollbackSkill(
    agent: string,
    skill: string,
    version: string,
    note: string,
  ): Promise<SkillVersion>;
  /** GET /agents-page/access: may this user open the Agents page, and is history on. */
  getAgentsPageAccess(signal?: AbortSignal): Promise<AgentsPageAccess>;
  /** GET /activity: one page of run history; `cursor` is the previous page's nextCursor. */
  getActivity(
    filters: ActivityFilters,
    cursor: string | null,
    signal?: AbortSignal,
  ): Promise<ActivityPage>;
  /** GET /activity/{id}: one run in full. */
  getActivityItem(id: string, signal?: AbortSignal): Promise<ActivityItem>;
  /** GET /mimir/channels: the channels Mimir is in (an approver only). */
  listMimirChannels(signal?: AbortSignal): Promise<MimirChannels>;
  /**
   * POST /mimir/sends/preview: exactly what Mimir would post. A refusal (even a
   * 4xx or 5xx) comes back as a MimirOutcome with ok false and its code.
   */
  previewMimirSend(
    input: MimirSendInput,
    signal?: AbortSignal,
  ): Promise<MimirPreview | MimirOutcome>;
  /** POST /mimir/sends: send what the preview showed (`sha256` is its hash). Refusals come back as ok false. */
  sendMimirSend(
    input: MimirSendInput & { sha256: string },
    signal?: AbortSignal,
  ): Promise<MimirOutcome>;
  /** GET /agents/{agent}/memory: the caller's own preference items. */
  listMemory(agent: string, signal?: AbortSignal): Promise<MemoryList>;
  /** DELETE /agents/{agent}/memory/{itemId} (204). */
  deleteMemory(agent: string, itemId: string): Promise<void>;
};

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (ms <= 0 || signal.aborted) {
      resolve();
      return;
    }
    const finish = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const timer = setTimeout(finish, ms);
    signal.addEventListener('abort', finish, { once: true });
  });

export function createAgentClient(options: AgentClientOptions): AgentClient {
  const { baseUrl, getToken, maxReconnects = 3, retryDelayMs = 1000 } = options;
  const doFetch: typeof fetch =
    options.fetchImpl ?? ((input, init) => fetch(input, init));

  // The JWT goes in this header and nowhere else: not in URLs, errors or logs.
  const auth = (): Record<string, string> => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  // The 202 reply's eventsUrl is this same path (Plan 3a contract).
  const runUrl = (runId: string, path: string) =>
    `${baseUrl}/runs/${encodeURIComponent(runId)}/${path}`;

  const seg = encodeURIComponent;

  /** One Agents-view GET: bearer token, error mapping, body read by `read`. */
  async function getAgents<T>(
    path: string,
    read: (body: unknown) => T | null,
    signal?: AbortSignal,
    fail: (status: number, body: unknown) => AgentApiError = (status, body) =>
      agentsError(status, toErrorCode(body)),
  ): Promise<T> {
    const res = await doFetch(`${baseUrl}${path}`, {
      headers: { ...auth() },
      signal,
    });
    const body: unknown = await res.json().catch(() => null);
    if (!res.ok) throw fail(res.status, body);
    const result = read(body);
    if (!result) throw new AgentApiError('http', MESSAGES.badReply);
    return result;
  }

  /** One skill-edit call: JSON body optional, reply read by `read` (null for a 204). */
  async function skillCall<T>(
    agent: string,
    skill: string,
    sub: string,
    method: string,
    payload: unknown,
    read: (body: unknown) => T | null,
  ): Promise<T> {
    const res = await doFetch(
      `${baseUrl}/agents/${seg(agent)}/skills/${seg(skill)}/${sub}`,
      {
        method,
        headers: {
          ...(payload === undefined
            ? {}
            : { 'Content-Type': 'application/json' }),
          ...auth(),
        },
        ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
      },
    );
    const body: unknown = await res.json().catch(() => null);
    if (!res.ok) throw skillError(res.status, body);
    const result = read(body);
    if (result === null) throw new AgentApiError('http', MESSAGES.badReply);
    return result;
  }

  /** One Mimir POST. The body carries only the fields of its kind: the service refuses the rest. */
  async function mimirPost(
    path: string,
    input: MimirSendInput & { sha256?: string },
    signal?: AbortSignal,
  ): Promise<{ ok: boolean; body: unknown }> {
    const { channel, message, intro, draft, text, threadLink, sha256 } = input;
    const thread = threadLink?.trim() ? { threadLink: threadLink.trim() } : {};
    const payload =
      message === 'intro'
        ? {
            channel,
            message,
            intro,
            ...(draft ? { draft } : {}),
            ...thread,
            sha256,
          }
        : { channel, message, text, ...thread, sha256 };
    const res = await doFetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...auth() },
      body: JSON.stringify(payload),
      signal,
    });
    const body = await res.json().catch(() => null);
    // The sign-in refusals come before the handler runs, so nothing was posted or recorded.
    if (res.status === 401) throw new AgentApiError('access', MESSAGES.access);
    if (
      res.status === 503 &&
      (body as { detail?: unknown } | null)?.detail === 'auth_unavailable'
    )
      throw new AgentApiError('http', MESSAGES.authUnavailable);
    return { ok: res.ok, body };
  }

  /** One CA lead POST. A network failure becomes the client's own unreachable error. */
  async function caPost(
    path: string,
    payload: unknown,
  ): Promise<{ ok: boolean; status: number; body: unknown }> {
    let res: Response;
    try {
      res = await doFetch(`${baseUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify(payload),
      });
    } catch {
      throw new AgentApiError('lost', MESSAGES.unreachable);
    }
    const body: unknown = await res.json().catch(() => null);
    return { ok: res.ok, status: res.status, body };
  }

  /** A CA lead reply with no `outcome`: the message is written here, never copied from the body. */
  function caError(status: number, body: unknown): AgentApiError {
    const code = toErrorCode(body);
    if (status === 401) return new AgentApiError('access', MESSAGES.access);
    if (status === 403)
      return new AgentApiError('access', MESSAGES.caNotAllowed);
    if (status === 404 && code === 'unknown_run')
      return new AgentApiError('run_gone', MESSAGES.caUnknownRun);
    if (status === 503)
      return new AgentApiError('http', MESSAGES.toolsUnavailable);
    return new AgentApiError('http', MESSAGES.badReply);
  }

  return {
    listMimirChannels: (signal) =>
      getAgents('/mimir/channels', toMimirChannels, signal, mimirError),
    async previewMimirSend(input, signal) {
      const { ok, body } = await mimirPost(
        '/mimir/sends/preview',
        input,
        signal,
      );
      if (!ok) return toMimirRefusal(body);
      return toMimirPreview(body) ?? toMimirRefusal(null);
    },
    async sendMimirSend(input, signal) {
      return toMimirSendOutcome(
        (await mimirPost('/mimir/sends', input, signal)).body,
      );
    },
    getSkillHistory: (agent, skill, signal) =>
      getAgents(
        `/agents/${seg(agent)}/skills/${seg(skill)}/versions`,
        toSkillHistory,
        signal,
        skillError,
      ),
    saveSkillDraft: (agent, skill, text, copiedFrom) =>
      skillCall(
        agent,
        skill,
        'draft',
        'PUT',
        copiedFrom ? { text, copiedFrom } : { text },
        toSkillDraft,
      ),
    async discardSkillDraft(agent, skill) {
      const res = await doFetch(
        `${baseUrl}/agents/${seg(agent)}/skills/${seg(skill)}/draft`,
        { method: 'DELETE', headers: { ...auth() } },
      );
      if (res.ok) return;
      const body: unknown = await res.json().catch(() => null);
      throw skillError(res.status, body);
    },
    publishSkill: (agent, skill, reason) =>
      skillCall(agent, skill, 'publish', 'POST', { reason }, toSkillVersion),
    rollbackSkill: (agent, skill, version, note) =>
      skillCall(
        agent,
        skill,
        'rollback',
        'POST',
        { version, note },
        toSkillVersion,
      ),
    listAgents: (signal) => getAgents('/agents', toAgentsCatalog, signal),
    getSkill: (agent, skill, signal) =>
      getAgents(
        `/agents/${seg(agent)}/skills/${seg(skill)}`,
        toSkillDoc,
        signal,
        skillError,
      ),
    getAgentsPageAccess: (signal) =>
      getAgents(
        '/agents-page/access',
        toAgentsPageAccess,
        signal,
        activityError,
      ),
    getActivity: (filters, cursor, signal) =>
      getAgents(
        `/activity?${activityQuery(filters, cursor)}`,
        toActivityPage,
        signal,
        activityError,
      ),
    getActivityItem: (id, signal) =>
      getAgents(`/activity/${seg(id)}`, toActivityItem, signal, activityError),
    listMemory: (agent, signal) =>
      getAgents(`/agents/${seg(agent)}/memory`, toMemoryList, signal),

    async deleteMemory(agent, itemId) {
      const res = await doFetch(
        `${baseUrl}/agents/${seg(agent)}/memory/${seg(itemId)}`,
        { method: 'DELETE', headers: { ...auth() } },
      );
      if (res.ok) return;
      const body: unknown = await res.json().catch(() => null);
      throw agentsError(res.status, toErrorCode(body));
    },

    async startChat(request, signal) {
      const res = await doFetch(`${baseUrl}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify(toChatBody(request)),
        signal,
      });
      if (res.status === 401 || res.status === 403) {
        throw new AgentApiError('access', MESSAGES.access);
      }
      if (res.status === 409) throw new AgentApiError('http', MESSAGES.busy);
      if (res.status === 503)
        throw new AgentApiError('http', MESSAGES.authUnavailable);
      if (!res.ok) throw httpError(res.status);
      const accepted = toChatAccepted(await res.json().catch(() => null));
      if (!accepted) throw new AgentApiError('http', MESSAGES.badReply);
      return accepted;
    },

    async uploadPricingSheet({ file, lenderId, sessionId }, signal) {
      const form = new FormData();
      form.append('file', file);
      form.append('lenderId', lenderId);
      if (sessionId) form.append('sessionId', sessionId);
      // No Content-Type: the browser adds multipart/form-data with its boundary.
      const res = await doFetch(`${baseUrl}/pricing-imports`, {
        method: 'POST',
        headers: { ...auth() },
        body: form,
        signal,
      });
      if (!res.ok) {
        const body: unknown = await res.json().catch(() => null);
        throw uploadError(res.status, toErrorCode(body));
      }
      const accepted = toChatAccepted(await res.json().catch(() => null));
      if (!accepted) throw new AgentApiError('http', MESSAGES.badReply);
      return accepted;
    },

    async streamEvents(runId, handlers, signal) {
      const seen = new Set<string>();
      let lastEventId: string | undefined;
      let fruitless = 0;

      while (!signal.aborted) {
        let progressed = false;
        let finished = false;
        // An error thrown by the consumer's onEvent. It is the consumer's bug,
        // not a dropped connection, so it must not be swallowed as a cut below.
        let handlerFailure: { error: unknown } | undefined;
        try {
          const res = await doFetch(runUrl(runId, 'events'), {
            headers: {
              Accept: 'text/event-stream',
              ...auth(),
              ...(lastEventId ? { 'Last-Event-ID': lastEventId } : {}),
            },
            cache: 'no-store',
            signal,
          });
          // Not a stream we will read: free the connection before throwing or retrying.
          if (!res.ok) res.body?.cancel().catch(() => undefined);
          if (res.status === 401 || res.status === 403) {
            throw new AgentApiError('access', MESSAGES.access);
          }
          if (res.status === 404) {
            throw new AgentApiError('run_gone', MESSAGES.runGone);
          }
          if (res.ok && res.body) {
            const reader = res.body.getReader();
            // Fresh decoder and parser per connection: a half-read frame from a
            // cut connection must not be glued onto the next one's bytes.
            const decoder = new TextDecoder();
            const parser = createSseParser((frame) => {
              if (finished) return;
              // An empty `id:` counts as no id (never sent back as Last-Event-ID).
              if (frame.id) {
                if (seen.has(frame.id)) return; // a resume repeated it
                seen.add(frame.id);
                lastEventId = frame.id;
                // Any new id is progress, even for an event type this panel
                // doesn't know yet (later plans add some), so it can't make us
                // give up early.
                progressed = true;
              }
              let event = toAgentEvent(frame.event, frame.data);
              // wire.ts drops a done/error whose data isn't JSON. The server did
              // mean to end the run, so end it with a generic failure rather than
              // reconnecting for a closing frame that will never parse.
              if (
                !event &&
                (frame.event === 'done' || frame.event === 'error')
              ) {
                event = {
                  type: 'error',
                  code: 'internal',
                  message: GENERIC_FAILURE,
                  runId: '',
                };
              }
              if (!event) return;
              progressed = true;
              try {
                handlers.onEvent(event);
              } catch (error) {
                // Stop reading (the finally below cancels the reader) and hand
                // this very error back to the caller once the attempt unwinds.
                handlerFailure = { error };
                finished = true;
                return;
              }
              // done ends the run; error is always followed by done, so stop there too.
              if (event.type === 'done' || event.type === 'error')
                finished = true;
            });
            try {
              while (!finished) {
                const { value, done } = await reader.read();
                if (done) break;
                parser.push(decoder.decode(value, { stream: true }));
              }
            } finally {
              if (finished) reader.cancel().catch(() => undefined);
            }
          }
          // Any other status (503 auth_unavailable, an ingress 502), an empty
          // stream, or the server's ~200 s close falls through as a cut.
        } catch (error) {
          if (error instanceof AgentApiError) throw error;
          // A network error or a stream cut mid-read: reconnect below.
        }
        if (handlerFailure) throw handlerFailure.error;
        if (finished || signal.aborted) return;
        fruitless = progressed ? 0 : fruitless + 1;
        if (fruitless > maxReconnects) {
          throw new AgentApiError('lost', MESSAGES.lost);
        }
        handlers.onReconnect?.();
        await sleep(retryDelayMs, signal);
      }
    },

    async cardAction(runId, cardId, action, reason, signal, selected) {
      const res = await doFetch(
        runUrl(runId, `cards/${encodeURIComponent(cardId)}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...auth() },
          body: JSON.stringify(
            toCardActionBody(action, reason?.trim(), selected),
          ),
          signal,
        },
      );
      const body: unknown = await res.json().catch(() => null);
      if (!res.ok) throw cardError(res.status, toErrorCode(body));
      const result = toCardActionResult(body);
      if (!result) throw new AgentApiError('http', MESSAGES.badReply);
      return result;
    },

    async suggestCa(assessmentId) {
      const res = await caPost('/ca-lead/suggest', { assessmentId });
      const body = res.body as Partial<CaSuggestionWire> | null;
      if (!res.ok) throw caError(res.status, body);
      if (
        !body ||
        typeof body.runId !== 'string' ||
        typeof body.status !== 'string'
      )
        throw new AgentApiError('http', MESSAGES.badReply);
      return {
        ...(body as CaSuggestionWire),
        // Only an explicit true means leave was checked; anything else must not read as "not on leave".
        leaveChecked: body.leaveChecked === true,
        complexitySource: parseComplexitySource(body.complexitySource),
        complexityUpgraded: body.complexityUpgraded === true,
        eligible: Array.isArray(body.eligible) ? body.eligible : [],
        notEligible: Array.isArray(body.notEligible) ? body.notEligible : [],
      };
    },

    async assignCa({ runId, creditAnalystId, reasonCode }) {
      const res = await caPost('/ca-lead/assign', {
        runId,
        creditAnalystId: Number(creditAnalystId),
        ...(reasonCode ? { reasonCode } : {}),
      });
      const body = res.body as {
        outcome?: unknown;
        hubspotSync?: unknown;
        decided?: unknown;
      } | null;
      if (typeof body?.outcome === 'string')
        return {
          outcome: body.outcome,
          hubspotSync:
            typeof body.hubspotSync === 'string' ? body.hubspotSync : null,
          decided: typeof body.decided === 'string' ? body.decided : null,
        };
      throw caError(res.status, body);
    },

    async sendFeedback(runId, rating) {
      try {
        const res = await doFetch(runUrl(runId, 'feedback'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...auth() },
          body: JSON.stringify(toFeedbackBody(rating)),
        });
        return res.ok;
      } catch {
        return false;
      }
    },
  };
}
